import * as PIXI from 'pixi.js';
import { LayoutContainer, LayoutText } from '@pixi/layout/components'; // eslint-disable-line
import { APP_WIDTH, APP_HEIGHT, CARD_WIDTH, CARD_HEIGHT } from '../../utils';

import Tappable from '../../common/containers/Tappable';
import BottomBar, { BOTTOM_BAR_HEIGHT } from '../../common/containers/BottomBar';
import PackOpenAnimatorContainer from '../containers/packOpenAnimatorContainer';
import ShopItemDetailContainer from '../containers/shopItemDetailContainer';

const TOP_BAR_HEIGHT = 120;
const HERO_IMAGE_HEIGHT = 440;
const HERO_BOTTOM_BORDER_HEIGHT = 10;
const ITEM_CARD_SCALE = 1.1;
const LIST_PADDING = 40;
const COINS_BOX_WIDTH = 320;
const COINS_BOX_HEIGHT = 70;
const ITEM_GAP = 20;
const SECTION_TITLE_HEIGHT = 120;
const SECTION_GAP = 16;
const CARD_PADDING = 16;
const CARD_INNER_GAP = 10;
const MAX_ITEM_CARD_WIDTH = 420;

function redrawRect(graphics, width, height, fill) {
    graphics.clear();
    graphics.rect(0, 0, width, height);
    graphics.fill(fill);
}

function itemCardWidth(contentWidth, count) {
    const columns = Math.max(count, 1);
    const fit = Math.floor((contentWidth - ITEM_GAP * (columns - 1)) / columns);
    return Math.min(MAX_ITEM_CARD_WIDTH, Math.max(fit, 1));
}

function itemCardMetrics(cardWidth) {
    const imageScale = Math.min(ITEM_CARD_SCALE, (cardWidth - CARD_PADDING * 2) / CARD_WIDTH);
    const imageWidth = 300;
    const imageHeight = 450;
    const textWidth = cardWidth - CARD_PADDING * 2;
    const nameHeight = 50;
    const lineHeight = 36;
    const cardHeight = CARD_PADDING * 2
        + imageHeight
        + nameHeight
        + lineHeight * 2
        + CARD_INNER_GAP * 3;

    return { imageWidth, imageHeight, textWidth, nameHeight, lineHeight, cardHeight };
}

export default class ShopManager {
    constructor(items = [], coins = 0) {
        this.container = new PIXI.Container();
        this.container.label = 'shopContainer';
        this.items = items;
        this.coins = coins;
        this.width = APP_WIDTH;
        this.height = APP_HEIGHT;
        this.claimingItemId = null;
        this.packOpenAnimator = null;
        this.itemDetail = null;
        this.shopBadgeVisible = false;
    }

    static async loadStaticAssets() {
        await PIXI.Assets.load([
            { src: '/assets/shop/header.png', alias: 'shopHeader' },
            { src: '/assets/shop/coin.png', alias: 'coin' },
        ]);
        await BottomBar.loadAssets();
        await ShopItemDetailContainer.loadAssets();
    }

    static async loadItemAssets(items) {
        const assets = [];
        for (const item of items || []) {
            if (item.leader) {
                assets.push({ src: `/proxy/image/${item.leader}.png` });
            }
            if (item.image) {
                assets.push({ src: item.image });
            }
        }
        if (assets.length > 0) {
            await PIXI.Assets.load(assets);
        }
    }

    async loadAssets() {
        await ShopManager.loadStaticAssets();
        await ShopManager.loadItemAssets(this.items);
    }

    attachToPixiApp(app) {
        app.stage.addChild(this.container);
    }

    detachFromPixiApp(app) {
        this.hideOpenedCards();
        app.stage.removeChild(this.container);
    }

    setItems(items) {
        this.items = items || [];
        this.buildItemList();
    }

    setClaimingItemId(itemId) {
        this.claimingItemId = itemId;
        this.buildItemList();
    }

    coinsLabel() {
        return `${Number(this.coins).toLocaleString()}`;
    }

    setCoins(coins) {
        this.coins = coins || 0;
        if (this.coinsText) {
            this.coinsText.text = this.coinsLabel();
        }
    }

    /**
     * Plays the pack open sequence and resolves once the player collects the cards.
     * `onReady` fires when the animator's assets are in, so the caller can drop its loading state.
     */
    async showOpenedCards(openedCards, itemId, onReady) {
        if (!openedCards?.length) {
            return;
        }

        const pack = this.items.find((item) => item.id === itemId);
        this.packOpenAnimator = new PackOpenAnimatorContainer(
            { name: pack?.name, openedCards },
            { width: this.width, height: this.height },
        );
        await this.packOpenAnimator.loadAssets();
        onReady?.();
        this.container.addChild(this.packOpenAnimator);

        await this.packOpenAnimator.start();
        await new Promise((resolve) => {
            this.packOpenAnimator.onCollect = resolve;
        });

        this.hideOpenedCards();
        this.onDismissOpenedCards?.();
    }

    hideOpenedCards() {
        if (this.packOpenAnimator) {
            // unblocks showOpenedCards when we tear down before the player collected
            this.packOpenAnimator.onCollect?.();
            this.container.removeChild(this.packOpenAnimator);
            this.packOpenAnimator.destroy({ children: true });
            this.packOpenAnimator = null;
        }
    }

    showItemDetail(item) {
        if (!item || this.claimingItemId != null) return;
        if (this.itemDetail?.item === item) return;

        this.hideItemDetail();
        const detail = new ShopItemDetailContainer(item, this.coins, this.width, this.height);
        detail.onBackgroundClicked = () => this.hideItemDetail();
        detail.onConfirm = () => {
            this.hideItemDetail();
            this.onClaimItem?.(item.id);
        };
        this.itemDetail = detail;
        this.container.addChild(detail);
    }

    hideItemDetail() {
        if (!this.itemDetail) return;
        this.itemDetail.parent?.removeChild(this.itemDetail);
        this.itemDetail.destroy();
        this.itemDetail = null;
    }

    buildItemCard(item, cardWidth, metrics) {
        const { imageWidth, imageHeight, textWidth, nameHeight } = metrics;

        const card = new Tappable();
        card.label = `shopItem-${item.id}`;
        card.layout = {
            width: cardWidth,
            flexDirection: 'column',
            alignItems: 'center',
        };
        card.onTap = () => this.showItemDetail(item);

        const content = new LayoutContainer();
        content.layout = {
            width: cardWidth,
            flexDirection: 'column',
            alignItems: 'center',
            gap: CARD_INNER_GAP,
            padding: CARD_PADDING,
            transformOrigin: 'center',
        };
        card.addChild(content);

        card.onHoldStart = () => {
            content.scale.set(0.9);
        };
        card.onHoldEnd = () => {
            content.scale.set(1);
        };

        if (item.image && PIXI.Assets.get(item.image)) {
            const itemSprite = new PIXI.Sprite(PIXI.Assets.get(item.image));
            itemSprite.layout = {
                width: imageWidth,
                height: imageHeight,
                objectFit: 'contain',
            };
            content.addChild(itemSprite);
        } else if (item.leader && PIXI.Assets.get(`/proxy/image/${item.leader}.png`)) {
            const leaderSprite = new PIXI.Sprite(PIXI.Assets.get(`/proxy/image/${item.leader}.png`));
            leaderSprite.layout = {
                width: imageWidth,
                height: imageHeight,
                objectFit: 'contain',
            };
            content.addChild(leaderSprite);
        } else if (item.type === 'pack') {
            const packPlaceholder = new LayoutContainer();
            packPlaceholder.layout = {
                width: imageWidth,
                height: imageHeight,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#3b72f5',
                borderRadius: 16,
            };
            const packLabel = new LayoutText({
                text: 'PACK',
                style: {
                    fontFamily: 'Open Sans',
                    fontSize: 40,
                    fontWeight: '700',
                    fill: '#ffffff',
                },
            });
            packPlaceholder.addChild(packLabel);
            content.addChild(packPlaceholder);
        }

        const nameText = new LayoutText({
            text: item.name,
            style: {
                fontFamily: 'Open Sans',
                fontSize: 45,
                fontWeight: '600',
                fill: '#7793c2',
                wordWrap: true,
                wordWrapWidth: textWidth,
                align: 'center',
            },
        });
        nameText.layout = { width: textWidth, height: nameHeight };
        content.addChild(nameText);

        const PRICE_PILL_WIDTH = 300;
        const PRICE_PILL_HEIGHT = 85;
        const isFree = item.price === 0 || item.nextClaimFree;

        const pillBackground = isFree ? new PIXI.Graphics() : undefined;
        if (isFree) {
            const innerBorderWidth = 5;
            const outerBorderWidth = 3;
            const radius = PRICE_PILL_HEIGHT / 2;
            pillBackground.roundRect(0, 0, PRICE_PILL_WIDTH, PRICE_PILL_HEIGHT, radius);
            pillBackground.fill(new PIXI.FillGradient({
                type: 'linear',
                start: { x: 0, y: 0 },
                end: { x: 0, y: 1 },
                colorStops: [
                    { offset: 0, color: '#2c5729' },
                    { offset: 1, color: '#6aa846' },
                ],
            }));
            pillBackground.stroke({
                fill: new PIXI.FillGradient({
                    type: 'linear',
                    start: { x: 0, y: 0 },
                    end: { x: 0, y: 1 },
                    colorStops: [
                        { offset: 0, color: '#6aa846' },
                        { offset: 1, color: '#2c5729' },
                    ],
                }),
                width: innerBorderWidth,
                alignment: 1,
            });
            pillBackground.roundRect(0, 0, PRICE_PILL_WIDTH, PRICE_PILL_HEIGHT, radius);
            pillBackground.stroke({ color: '#AEC886', width: outerBorderWidth, alignment: 0 });
            pillBackground.eventMode = 'none';
            pillBackground.layout = {
                position: 'absolute',
                width: PRICE_PILL_WIDTH,
                height: PRICE_PILL_HEIGHT,
                objectFit: 'none',
                objectPosition: 'center',
            };
        }

        const pillContainer = new LayoutContainer();
        pillContainer.layout = {
            width: PRICE_PILL_WIDTH,
            height: PRICE_PILL_HEIGHT,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: isFree ? undefined : '#131a2a',
            borderRadius: PRICE_PILL_HEIGHT / 2,
            borderColor: isFree ? undefined : '#475366',
            borderWidth: isFree ? 0 : 4,
            marginTop: 12,
            marginBottom: 0,
        };
        pillContainer.label = 'pricePill';
        content.addChild(pillContainer);
        if (pillBackground) pillContainer.addChild(pillBackground);

        const priceText = new LayoutText({
            text: isFree ? 'FREE' : `${Number(item.price).toLocaleString()}`,
            style: {
                fontFamily: 'Caladea',
                fontSize: 40,
                fontWeight: '700',
                fill: '#ffffff',
            },
            layout: {
                width: PRICE_PILL_WIDTH,
                height: PRICE_PILL_HEIGHT,
                objectPosition: 'center',
            },
        });
        pillContainer.addChild(priceText);

        if (!isFree) {
            const coinIcon = new PIXI.Sprite(PIXI.Assets.get('coin'));
            coinIcon.y = -2;
            coinIcon.layout = {
                width: 70,
                height: 71,
                // objectFit: 'contain',
                position: 'absolute',
                left: 5,
            };
            pillContainer.addChild(coinIcon);
        }

        return card;
    }

    buildItemSection(title, items, cardWidth, metrics) {
        const contentWidth = this.width - LIST_PADDING * 2;
        const section = new LayoutContainer();
        section.label = `shopSection-${title}`;
        section.layout = {
            width: contentWidth,
            flexDirection: 'column',
            gap: SECTION_GAP,
            flexShrink: 0,
        };

        const titleText = new LayoutText({
            text: title,
            style: {
                fontFamily: 'Caladea',
                fontSize: 55,
                fontWeight: 'bold',
                fill: '#e2eefa',
            },
        });
        titleText.layout = {
            width: contentWidth,
            height: SECTION_TITLE_HEIGHT,
        };
        section.addChild(titleText);

        const row = new LayoutContainer();
        row.layout = {
            width: contentWidth,
            // height: metrics.cardHeight,
            flexDirection: 'row',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: ITEM_GAP,
            paddingTop: 40,
        };


        const rowBackgroundGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: 'rgba(22, 32, 50, 1)' },
                { offset: 1, color: 'rgba(22, 32, 50, 0)' },
            ],
        });

        const rowBackgroundStrokeGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: 'rgba(41, 65, 106, 1)' },
                { offset: 1, color: 'rgba(41, 65, 106, 0)' },
            ],
        });

        const strokeWidth = 7;
        const rowBackgroundStroke = new PIXI.Graphics();
        rowBackgroundStroke.roundRect(0, -strokeWidth, contentWidth, metrics.cardHeight, 24);
        rowBackgroundStroke.stroke({ fill: rowBackgroundStrokeGradient, width: strokeWidth, alignment: 1 });
        row.addChild(rowBackgroundStroke);

        const rowBackground = new PIXI.Graphics();
        rowBackground.roundRect(0, 0, contentWidth, metrics.cardHeight, 24);
        rowBackground.fill(rowBackgroundGradient);
        rowBackground.roundRect(0, 0, contentWidth, metrics.cardHeight, 20);
        row.addChild(rowBackground);
        
        for (const item of items) {
            row.addChild(this.buildItemCard(item, cardWidth, metrics));
        }
        section.addChild(row);

        return section;
    }

    buildItemList() {
        if (!this.itemsListContainer) {
            return;
        }
        this.itemsListContainer.removeChildren();

        const packs = this.items.filter((item) => item.type === 'pack');
        const starters = this.items.filter((item) => item.type === 'starter_deck');
        const contentWidth = this.width - LIST_PADDING * 2;
        // const columnCount = Math.max(packs.length, starters.length, 1);
        const columnCount = 3;
        const cardWidth = itemCardWidth(contentWidth, columnCount);
        const metrics = itemCardMetrics(cardWidth);

        if (packs.length > 0) {
            this.itemsListContainer.addChild(this.buildItemSection('BOOSTER PACKS', packs, cardWidth, metrics));
        }
        if (starters.length > 0) {
            this.itemsListContainer.addChild(this.buildItemSection('STARTER DECKS', starters, cardWidth, metrics));
        }
    }

    buildTopBar() {
        this.topBarContainer = new LayoutContainer();
        this.topBarContainer.label = 'topBarContainer';
        this.topBarContainer.x = 0;
        this.topBarContainer.y = 0;
        this.topBarContainer.layout = {
            width: this.width,
            height: TOP_BAR_HEIGHT,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
        };
        this.container.addChild(this.topBarContainer);

        const backgroundGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#41557c' },
                { offset: 1, color: '#1f2b3f' },
            ],
        });

        this.topBarBackground = new PIXI.Graphics();
        redrawRect(this.topBarBackground, this.width, TOP_BAR_HEIGHT, backgroundGradient);
        this.topBarContainer.addChild(this.topBarBackground);

        const coinsContainer = new PIXI.Container();
        coinsContainer.label = 'shopCoinsContainer';
        coinsContainer.layout = {
            width: COINS_BOX_WIDTH,
            height: COINS_BOX_HEIGHT,
            right: 50,
            position: 'absolute',
        };
        this.topBarContainer.addChild(coinsContainer);

        const coinsBackground = new PIXI.Graphics();
        coinsBackground.roundRect(0, 0, COINS_BOX_WIDTH, COINS_BOX_HEIGHT, 50);
        coinsBackground.fill({ color: '#0d1119' });
        coinsBackground.stroke({ color: '#6d7e99', width: 3 });
        coinsContainer.addChild(coinsBackground);

        const coinIcon = new PIXI.Sprite(PIXI.Assets.get('coin'));
        coinIcon.x = -50;
        coinIcon.y = COINS_BOX_HEIGHT / 2;
        coinIcon.scale.set(1.5);
        coinIcon.anchor.set(0, 0.5);
        coinsContainer.addChild(coinIcon);

        this.coinsText = new PIXI.Text({
            text: this.coinsLabel(),
            style: {
                fontFamily: 'Caladea',
                fontSize: 38,
                fill: '#ffffff',
                fontWeight: 'bold',
            },
        });
        this.coinsText.label = 'shopCoinsText';
        this.coinsText.x = COINS_BOX_WIDTH / 2;
        this.coinsText.y = COINS_BOX_HEIGHT / 2;
        this.coinsText.anchor.set(0.5);
        coinsContainer.addChild(this.coinsText);
    }

    buildHeroImage() {
        this.heroImageContainer = new LayoutContainer();
        this.heroImageContainer.label = 'heroImageContainer';
        this.heroImageContainer.x = 0;
        this.heroImageContainer.y = TOP_BAR_HEIGHT;
        this.heroImageContainer.layout = {
            width: this.width,
            height: HERO_IMAGE_HEIGHT,
        };
        this.container.addChild(this.heroImageContainer);

        this.heroImage = new PIXI.Sprite(PIXI.Assets.get('shopHeader'));
        this.heroImage.label = 'heroImage';
        this.heroImage.anchor.set(0, 0);
        const scale = Math.max(
            1,
            this.width / this.heroImage.texture.width,
            HERO_IMAGE_HEIGHT / this.heroImage.texture.height,
        );
        this.heroImage.scale.set(scale);
        this.heroImage.position.set(0, 0);
        this.heroImageContainer.addChild(this.heroImage);

        const titleGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });

        this.topBarTitle = new LayoutText({
            text: 'Card Shop',
            style: {
                fontFamily: 'Caladea',
                fontSize: 50,
                fill: titleGradient,
                fontWeight: 'bold',
                // letterSpacing: 4,
                stroke: { color: '#422400', width: 5 },
            },
            layout: {
                width: 250,
                height: TOP_BAR_HEIGHT,
                position: 'absolute',
                left: 290,
                top: 93,
                objectPosition: 'left',
            },
        });
        this.heroImageContainer.addChild(this.topBarTitle);

        this.heroImageBottomBorder = new PIXI.Graphics();
        this.heroImageBottomBorder.label = 'heroImageBottomBorder';
        this.heroImageBottomBorder.x = 0;
        this.heroImageBottomBorder.y = TOP_BAR_HEIGHT + HERO_IMAGE_HEIGHT;
        redrawRect(this.heroImageBottomBorder, this.width, HERO_BOTTOM_BORDER_HEIGHT, { color: '#131c2b' });
        this.container.addChild(this.heroImageBottomBorder);
    }

    buildItemsList() {
        this.itemsListContainer = new LayoutContainer();
        this.itemsListContainer.label = 'shopItemsListContainer';
        this.itemsListContainer.x = 0;
        this.itemsListContainer.y = TOP_BAR_HEIGHT + HERO_IMAGE_HEIGHT + HERO_BOTTOM_BORDER_HEIGHT;
        this.itemsListContainer.layout = {
            width: this.width,
            height: this.listAreaHeight(),
            flexDirection: 'column',
            alignItems: 'center',
            overflow: 'scroll',
            gap: 30,
            padding: LIST_PADDING,
            paddingBottom: BOTTOM_BAR_HEIGHT - LIST_PADDING,
            backgroundColor: '#203250',
        };
        this.container.addChild(this.itemsListContainer);
    }

    buildBottomBar() {
        this.bottomBar = new BottomBar({
            width: this.width,
            selected: 'shop',
            onCollectionTap: () => this.onGoToCollectionButtonClick?.(),
            onMainTap: () => this.onBackButtonClick?.(),
            shopBadgeVisible: this.shopBadgeVisible,
        });
        this.bottomBar.y = this.height - BOTTOM_BAR_HEIGHT;
        this.container.addChild(this.bottomBar);
    }

    setShopBadgeVisible(visible) {
        this.shopBadgeVisible = visible;
        this.bottomBar?.setShopBadgeVisible(visible);
    }

    listAreaHeight() {
        return this.height - TOP_BAR_HEIGHT - HERO_IMAGE_HEIGHT - HERO_BOTTOM_BORDER_HEIGHT;
    }

    buildMainLayout(width, height) {
        const animator = this.packOpenAnimator;
        if (animator?.parent) {
            this.container.removeChild(animator);
        }

        for (const child of [...this.container.children]) {
            child.destroy({ children: true });
        }

        this.width = width;
        this.height = height;
        this.coinsText = null;
        this.itemsListContainer = null;

        this.buildTopBar();
        this.buildHeroImage();
        this.buildItemsList();
        this.buildBottomBar();
        this.buildItemList();

        // a pack open in progress has to survive the rebuild and follow the new size
        if (animator) {
            this.packOpenAnimator = animator;
            this.container.addChild(animator);
            animator.resize(width, height);
        }
    }
}
